import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { LastDayDateField } from "@/components/LastDayDateField";

export default function Register() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [form, setForm] = useState({
    email: params.get("email") || "",
    password: "",
    name: "",
    phone: "",
    mailing_address: "",
    city: "",
    state: "",
    zip_code: "",
    trailer_make: "",
    trailer_model: "",
    resort: "",
    lot_number: "",
    key_location: "",
    last_day_at_resort: "",
  });
  const [loading, setLoading] = useState(false);
  const [lateDateAcknowledged, setLateDateAcknowledged] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, late_date_acknowledged: lateDateAcknowledged });
      setSession(data.token, data.user);
      toast.success("Account created! Let's get you signed up.");
      navigate("/signup");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="A few quick details and you're ready to winterize."
      footer={
        <span>
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-secondary underline">Log in</Link>
        </span>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="register-name-input" required value={form.name} onChange={set("name")} className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" data-testid="register-phone-input" value={form.phone} onChange={set("phone")} className="h-11" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} className="h-11" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} className="h-11" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="mailing_address">Mailing address (street)</Label>
          <Input id="mailing_address" data-testid="register-address-input" value={form.mailing_address} onChange={set("mailing_address")} className="h-11" />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="city">City</Label>
            <Input id="city" data-testid="register-city-input" value={form.city} onChange={set("city")} className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <Input id="state" data-testid="register-state-input" value={form.state} onChange={set("state")} className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="zip_code">Zip</Label>
            <Input id="zip_code" data-testid="register-zip-input" value={form.zip_code} onChange={set("zip_code")} className="h-11" />
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="trailer_make">Trailer make</Label>
            <Input id="trailer_make" data-testid="register-make-input" value={form.trailer_make} onChange={set("trailer_make")} className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="trailer_model">Trailer model</Label>
            <Input id="trailer_model" data-testid="register-model-input" value={form.trailer_model} onChange={set("trailer_model")} className="h-11" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="resort">Resort (trailer location)</Label>
          <Input id="resort" data-testid="register-resort-input" value={form.resort} onChange={set("resort")} className="h-11" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="lot_number">Lot #</Label>
            <Input id="lot_number" data-testid="register-lot-input" value={form.lot_number} onChange={set("lot_number")} className="h-11" />
          </div>
          <LastDayDateField value={form.last_day_at_resort}
            onChange={(value) => setForm({ ...form, last_day_at_resort: value })}
            onAcknowledgedChange={setLateDateAcknowledged} acknowledged={lateDateAcknowledged}
            testId="register-lastday-input" required />
        </div>
        <div className="space-y-2">
          <Label htmlFor="key_location">Key location (hiding spot) <span className="text-destructive">*</span></Label>
          <Input id="key_location" data-testid="register-key-input" required value={form.key_location} onChange={set("key_location")} className="h-11" />
        </div>
        <Button data-testid="register-submit-btn" type="submit" disabled={loading} className="h-11 w-full rounded-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
        </Button>
      </form>
    </AuthLayout>
  );
}
